import { Link } from "@inertiajs/react"
import { ListChecks } from "lucide-react"
import { TableCard } from "@/components/blocks/TableCard"
import { Badge } from "@/components/catalyst/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/catalyst/table"
import { useIntl } from "react-intl"
import { useFormat } from "@/lib/format"
import type { QuizSummary } from "@/types/quizzes"

/** Quizzes in a table card; the title link stretches over the row. */
export function QuizList({
  quizzes,
  hrefFor,
  showStatus = false,
}: {
  quizzes: QuizSummary[]
  hrefFor: (quiz: QuizSummary) => string
  showStatus?: boolean
}) {
  const intl = useIntl()
  const format = useFormat()
  return (
    <TableCard>
      <Table bleed rowLinks className="[--gutter:--spacing(5)] [&_tbody_tr:last-child_td]:border-b-0">
        <TableHead>
          <TableRow>
            <TableHeader>{intl.formatMessage({ id: "quizzes.list_quiz" })}</TableHeader>
            <TableHeader className="hidden sm:table-cell">{intl.formatMessage({ id: "quizzes.list_questions" })}</TableHeader>
            <TableHeader className="text-right">{showStatus ? intl.formatMessage({ id: "quizzes.list_status" }) : intl.formatMessage({ id: "quizzes.list_best" })}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {quizzes.map((quiz) => (
            <TableRow key={quiz.id}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <ListChecks className="size-4 shrink-0 text-ink-muted" />
                  <div className="min-w-0">
                    <Link href={hrefFor(quiz)} className="font-medium text-ink-display no-underline after:absolute after:inset-0 hover:text-ink-display">
                      {quiz.title}
                    </Link>
                    {quiz.description && <div className="max-w-xs truncate text-xs text-ink-muted">{quiz.description}</div>}
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden text-ink-muted sm:table-cell">{format.questionCount(quiz.question_count)}</TableCell>
              <TableCell className="text-right">
                {showStatus ? (
                  <span className="inline-flex items-center gap-2">
                    {quiz.ai_draft && <Badge color="amber">{intl.formatMessage({ id: "quizzes.ai_draft" })}</Badge>}
                    <Badge color={quiz.status === "published" ? "accent" : "zinc"}>
                      {quiz.status === "published" ? intl.formatMessage({ id: "quizzes.published" }) : intl.formatMessage({ id: "quizzes.draft" })}
                    </Badge>
                  </span>
                ) : (
                  <span className="text-ink-muted">{format.bestScore(quiz.best_score)}</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableCard>
  )
}
